import { Dialog } from '@base-ui/react/dialog';
import { Drawer } from '@base-ui/react/drawer';
import { cn } from '@foundation/utils/cn';
import {
  ComponentPropsWithoutRef,
  createContext,
  ReactElement,
  ReactNode,
  useContext,
  useEffect,
} from 'react';

import { ensurePortalRoot } from '../primitives/ensurePortalRoot';
import { useMediaQuery } from '../primitives/useMediaQuery';

export type HeadlessModalStyles = {
  readonly container?: string;
  readonly overlay?: string;
  readonly panel?: string;
  readonly swipeArea?: string;
  readonly swipeHandle?: string;
  readonly closeButton?: string;
  readonly closeButtonContainer?: string;
  readonly header?: string;
  readonly body?: string;
  readonly buttonGroup?: string;
  readonly title?: string;
  readonly description?: string;
};

export type HeadlessModalRootProps = {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly children: ReactNode;
  readonly closePrevented?: boolean;
  readonly showCloseButton?: boolean;
  readonly disableDefaultAnimation?: boolean;
  readonly swipeDownToClose?: boolean;
  readonly initialFocus?: ComponentPropsWithoutRef<
    typeof Dialog.Popup
  >['initialFocus'];
  readonly finalFocus?: ComponentPropsWithoutRef<
    typeof Dialog.Popup
  >['finalFocus'];
  readonly closeLabel?: string;
  readonly closeButton?: ReactElement;
  readonly styles?: HeadlessModalStyles;
  readonly portalId?: string;
  readonly 'data-testid'?: string;
};

type HeadlessModalMode = 'dialog' | 'drawer';

type HeadlessModalContextValue = {
  readonly styles: HeadlessModalStyles;
  readonly mode: HeadlessModalMode;
};

const HeadlessModalContext = createContext<HeadlessModalContextValue | null>(
  null,
);

function useHeadlessModalContext(component: string): HeadlessModalContextValue {
  const context = useContext(HeadlessModalContext);
  if (!context) {
    throw new Error(`${component} must be used within HeadlessModal.Root`);
  }
  return context;
}

// Below `md` (768px, matches --breakpoint-md) the modal renders as a native swipeable bottom sheet
const DESKTOP_QUERY = '(min-width: 768px)';

function Root({
  isOpen,
  onClose,
  children,
  closePrevented = false,
  showCloseButton = true,
  disableDefaultAnimation = false,
  swipeDownToClose = true,
  initialFocus,
  finalFocus,
  closeLabel = 'Close',
  closeButton,
  styles = {},
  portalId = 'pmi-modal-root',
  'data-testid': dataTestId,
}: HeadlessModalRootProps) {
  const isDesktop = useMediaQuery(DESKTOP_QUERY);
  const mode: HeadlessModalMode = isDesktop ? 'dialog' : 'drawer';

  useEffect(() => {
    if (!isOpen) return;

    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Only close on `click` (not `pointerup`/`touchend`): those fire before `click` and would
  // unmount the backdrop first, letting the browser's subsequent click event fall through to
  // whatever element is now underneath the pointer.
  const handleBackdropInteraction = (event: React.MouseEvent) => {
    if (closePrevented) return;
    if (event.target !== event.currentTarget) return;
    onClose();
  };

  const closeButtonSlot = showCloseButton && !closePrevented && (
    <div
      className={cn('absolute right-0 top-0 z-20', styles.closeButtonContainer)}
    >
      {mode === 'dialog' ? (
        <Dialog.Close
          render={closeButton}
          className={cn('cursor-pointer', styles.closeButton)}
          aria-label={closeLabel}
          data-testid={dataTestId ? `${dataTestId}-close-button` : undefined}
        />
      ) : (
        <Drawer.Close
          render={closeButton}
          className={cn('cursor-pointer', styles.closeButton)}
          aria-label={closeLabel}
          data-testid={dataTestId ? `${dataTestId}-close-button` : undefined}
        />
      )}
    </div>
  );

  if (mode === 'dialog') {
    return (
      <HeadlessModalContext.Provider value={{ styles, mode }}>
        <Dialog.Root
          open={isOpen}
          onOpenChange={(nextOpen, eventDetails) => {
            if (nextOpen) return;
            if (closePrevented && eventDetails.reason !== 'close-press') return;
            onClose();
          }}
        >
          <Dialog.Portal container={ensurePortalRoot(portalId)}>
            <Dialog.Backdrop
              className={cn('fixed inset-0 z-40', styles.overlay)}
              data-testid={dataTestId ? `${dataTestId}-overlay` : undefined}
            />

            <Dialog.Popup
              className={cn(
                'fixed inset-0 z-50 flex items-center justify-center',
                styles.container,
              )}
              onClick={handleBackdropInteraction}
              initialFocus={initialFocus}
              finalFocus={finalFocus}
              data-testid={dataTestId ? `${dataTestId}-backdrop` : undefined}
            >
              <div
                data-testid={dataTestId}
                className={cn(
                  'pointer-events-auto relative w-full flex flex-col overflow-hidden',
                  !disableDefaultAnimation && 'animate-fade-in',
                  styles.panel,
                )}
              >
                {closeButtonSlot}
                {children}
              </div>
            </Dialog.Popup>
          </Dialog.Portal>
        </Dialog.Root>
      </HeadlessModalContext.Provider>
    );
  }

  return (
    <HeadlessModalContext.Provider value={{ styles, mode }}>
      <Drawer.Root
        open={isOpen}
        swipeDirection="down"
        onOpenChange={(nextOpen, eventDetails) => {
          if (nextOpen) return;
          if (closePrevented && eventDetails.reason !== 'close-press') return;
          if (!swipeDownToClose && eventDetails.reason === 'swipe') {
            eventDetails.cancel();
            return;
          }
          onClose();
        }}
      >
        <Drawer.Portal container={ensurePortalRoot(portalId)}>
          <Drawer.Backdrop
            className={cn(
              'fixed inset-0 z-40 transition-opacity duration-300 ease-out',
              'data-starting-style:opacity-0 data-ending-style:opacity-0',
              styles.overlay,
            )}
            data-testid={dataTestId ? `${dataTestId}-overlay` : undefined}
          />

          <Drawer.Viewport
            className={cn(
              'fixed inset-0 z-50 flex items-end justify-center',
              styles.container,
            )}
            onClick={handleBackdropInteraction}
            data-testid={dataTestId ? `${dataTestId}-backdrop` : undefined}
          >
            <Drawer.Popup
              initialFocus={initialFocus}
              finalFocus={finalFocus}
              data-testid={dataTestId}
              className={cn(
                'pointer-events-auto relative flex w-full flex-col overflow-hidden',
                // Base UI drives this with an inline `transform` while swiping (overrides these
                // classes); once released, the classes take over the hand-off to open/close
                '[transform:translateY(var(--drawer-swipe-movement-y,0px))]',
                !disableDefaultAnimation &&
                  'transition-transform duration-300 ease-out',
                'data-starting-style:[transform:translateY(100%)] data-ending-style:[transform:translateY(100%)]',
                styles.panel,
              )}
            >
              {swipeDownToClose && !closePrevented && (
                <div
                  className={cn(
                    'relative z-10 flex h-10 w-full shrink-0 items-center justify-center',
                    styles.swipeArea,
                  )}
                  data-testid={
                    dataTestId ? `${dataTestId}-swipe-area` : undefined
                  }
                >
                  <div
                    className={cn('pointer-events-none', styles.swipeHandle)}
                  />
                </div>
              )}

              {closeButtonSlot}

              {/* Drawer.Content opts mouse/pen out of swipe-dismiss (touch is unaffected) */}
              <Drawer.Content className="flex min-h-0 flex-1 flex-col">
                {children}
              </Drawer.Content>
            </Drawer.Popup>
          </Drawer.Viewport>
        </Drawer.Portal>
      </Drawer.Root>
    </HeadlessModalContext.Provider>
  );
}

function Header({ className, ...props }: ComponentPropsWithoutRef<'header'>) {
  const { styles } = useHeadlessModalContext('HeadlessModal.Header');
  return (
    <header
      className={cn('flex items-center gap-2', styles.header, className)}
      {...props}
    />
  );
}

function Body({ className, ...props }: ComponentPropsWithoutRef<'section'>) {
  const { styles } = useHeadlessModalContext('HeadlessModal.Body');
  return (
    <section
      className={cn('flex-1 overflow-y-auto', styles.body, className)}
      {...props}
    />
  );
}

function ButtonGroup({
  className,
  ...props
}: ComponentPropsWithoutRef<'footer'>) {
  const { styles } = useHeadlessModalContext('HeadlessModal.ButtonGroup');
  return (
    <footer
      className={cn(styles.buttonGroup, className)}
      {...props}
    />
  );
}

function Title({
  className,
  ...props
}: ComponentPropsWithoutRef<typeof Dialog.Title>) {
  const { styles, mode } = useHeadlessModalContext('HeadlessModal.Title');
  const TitlePrimitive = mode === 'dialog' ? Dialog.Title : Drawer.Title;
  return (
    <TitlePrimitive
      className={cn(styles.title, className)}
      {...props}
    />
  );
}

function Description({
  className,
  ...props
}: ComponentPropsWithoutRef<typeof Dialog.Description>) {
  const { styles, mode } = useHeadlessModalContext('HeadlessModal.Description');
  const DescriptionPrimitive =
    mode === 'dialog' ? Dialog.Description : Drawer.Description;
  return (
    <DescriptionPrimitive
      className={cn(styles.description, className)}
      {...props}
    />
  );
}

type CloseProps = {
  readonly render: ReactElement;
};

function Close({ render }: CloseProps) {
  const { mode } = useHeadlessModalContext('HeadlessModal.Close');

  return mode === 'dialog' ? (
    <Dialog.Close render={render} />
  ) : (
    <Drawer.Close render={render} />
  );
}

export const HeadlessModal = {
  Root,
  Header,
  Body,
  ButtonGroup,
  Title,
  Description,
  Close,
};
